import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Inbox, Mail, CheckCircle2, Archive, TrendingUp, ArrowRight, Newspaper, ImageIcon, Settings as SettingsIcon, BookOpen, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [stats, setStats] = useState({ new: 0, read: 0, responded: 0, archived: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/leads/stats`);
        setStats(data);
      } catch (err) {
        // Non-fatal: tiles will fall back to zero. Log so we notice if the
        // /admin/leads/stats endpoint regresses.
        console.warn("Dashboard: failed to load lead stats", err?.message || err);
      }
      setLoading(false);
    })();
  }, []);

  const handleDownloadGuide = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/downloads/admin-guide`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Antera_Admin_Training_Guide.docx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Guide downloaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  const cards = [
    { label: "New leads", value: stats.new, icon: Mail, color: "bg-sky-500" },
    { label: "Read", value: stats.read, icon: Inbox, color: "bg-indigo-500" },
    { label: "Responded", value: stats.responded, icon: CheckCircle2, color: "bg-emerald-500" },
    { label: "Archived", value: stats.archived, icon: Archive, color: "bg-slate-500" },
  ];

  const quickLinks = [
    { to: "/admin/site-settings", label: "Site Settings", desc: "Homepage, suppliers, testimonials, social.", icon: SettingsIcon, accent: "text-sky-600 bg-sky-50" },
    { to: "/admin/blog", label: "Blog Posts", desc: "Edit, tag, and publish articles.", icon: Newspaper, accent: "text-violet-600 bg-violet-50" },
    { to: "/admin/case-studies", label: "Case Studies", desc: "Customer success stories.", icon: BookOpen, accent: "text-emerald-600 bg-emerald-50" },
    { to: "/admin/media", label: "Media Library", desc: "Upload images and copy URLs.", icon: ImageIcon, accent: "text-amber-600 bg-amber-50" },
  ];

  return (
    <div data-testid="admin-dashboard" className="p-6 lg:p-10 max-w-6xl">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">An overview of your site activity.</p>
        </div>
        <Button
          onClick={handleDownloadGuide}
          disabled={downloading}
          data-testid="dashboard-download-guide"
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg h-10 text-xs font-semibold"
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />
          {downloading ? "Preparing\u2026" : "Download Training Guide"}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-5">
            <div className={`w-9 h-9 rounded-lg ${c.color} flex items-center justify-center mb-3`}>
              <c.icon className="h-4 w-4 text-white" />
            </div>
            <p className="text-2xl font-extrabold text-slate-900">{loading ? "\u2014" : c.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-sky-600" />
            <h2 className="text-base font-bold text-slate-900">Total submissions</h2>
          </div>
          <span className="text-3xl font-extrabold text-slate-900">{stats.total}</span>
        </div>
        <p className="text-sm text-slate-500">All-time contact form submissions across the site.</p>
        <Link
          to="/admin/leads"
          data-testid="dashboard-view-leads"
          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-sky-600 hover:gap-2 transition-all"
        >
          View leads inbox <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="mt-10">
        <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Manage content</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {quickLinks.map((q) => (
            <Link
              key={q.to}
              to={q.to}
              data-testid={`dashboard-link-${q.to.split("/").pop()}`}
              className="group bg-white border border-slate-200 rounded-xl p-4 hover:border-sky-300 hover:shadow-md transition-all"
            >
              <div className={`w-9 h-9 rounded-lg ${q.accent} flex items-center justify-center mb-3`}>
                <q.icon className="h-4 w-4" />
              </div>
              <p className="text-sm font-bold text-slate-900">{q.label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{q.desc}</p>
              <p className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-sky-600 group-hover:gap-2 transition-all">
                Open <ArrowRight className="h-3 w-3" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
